import React, { createContext, useContext, useState, useCallback } from 'react';
import {
  routerList,
  routerSaveOrUpdate,
  routerDelete,
} from '@/services/technical';

import apiService from '../services/index';

// 创建Context
const RouterPathContext = createContext();

/**
 * RouterPath状态管理Provider
 * 使用React内置状态管理替换Zustand
 */
// eslint-disable-next-line react/prop-types
export const RouterPathProvider = ({ children }) => {
  // ==================== 状态定义 ====================

  const [activeTab, setActiveTab] = useState('productLine');
  const [selectedProductLine, setSelectedProductLine] = useState(null);
  const [selectedProductModule, setSelectedProductModule] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [currentRecord, setCurrentRecord] = useState(null);
  const [routerData, setRouterData] = useState([]);
  const [productLines, setProductLines] = useState([]);
  const [productModules, setProductModules] = useState([]);
  const [siderCollapsed, setSiderCollapsed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // ==================== 基础操作方法 ====================

  /**
   * 设置选中的产品线
   */
  const handleSetSelectedProductLine = useCallback((productLine) => {
    setSelectedProductLine(productLine);
    // 切换产品线时清空产品模块选择
    setSelectedProductModule(null);
    setRouterData([]);
  }, []);
  /**
   * 设置选中的产品模块
   */
  const handleSetSelectedProductModule = useCallback((productModule) => {
    setSelectedProductModule(productModule);
    // 自动获取路由数据
    if (productModule) {
      fetchRouterData({ productModuleId: productModule.id });
    } else {
      setRouterData([]);
    }
  }, []);
  /**
   * 设置模态框显示状态
   */
  const handleSetModalVisible = useCallback((visible) => {
    setModalVisible(visible);
    // 关闭模态框时清空当前记录
    if (!visible) {
      setCurrentRecord(null);
    }
  }, []);

  /**
   * 切换侧边栏折叠状态
   */
  const toggleSiderCollapsed = useCallback(() => {
    setSiderCollapsed((prev) => !prev);
  }, []);
  /**
   * 清除错误状态
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // ==================== 数据操作方法 ====================

  /**
   * 获取路由数据
   */
  const fetchRouterData = useCallback(
    async (params = {}) => {
      setLoading(true);
      setError(null);
      const queryParams = {
        pageNum: 1,
        pageSize: 100,
        ...params,
      };
      // 如果有选中的产品线，添加到查询参数
      if (selectedProductLine) {
        queryParams.productLineId = selectedProductLine.id;
      }
      // 如果有选中的产品模块，添加到查询参数
      if (selectedProductModule) {
        queryParams.productModuleId = selectedProductModule.id;
      }
      try {
        const result = await routerList(queryParams);
        if (result.status === 'success') {
          setRouterData(result.data || []);
        } else {
          console.error('获取路由数据失败:', result.error);
          setError(result.error?.message || '获取数据失败');
          setRouterData([]);
        }
      } catch (err) {
        console.error('获取路由数据异常:', err);
        setError('获取数据异常');
        setRouterData([]);
      } finally {
        setLoading(false);
      }
    },
    [selectedProductLine, selectedProductModule],
  );

  /**
   * 更新路由数据
   */
  const updateRouterData = useCallback((newRouterData) => {
    setRouterData(newRouterData);
  }, []);
  /**
   * 添加路由数据
   */
  const addRouterData = useCallback((newRouter) => {
    setRouterData((prev) => [...prev, newRouter]);
  }, []);

  /**
   * 保存或更新路由
   */
  const saveOrUpdateRouter = useCallback(
    async (routerData) => {
      setLoading(true);
      setError(null);
      try {
        const result = await routerSaveOrUpdate(routerData);
        if (result.status === 'success') {
          // 保存成功后重新获取数据
          await fetchRouterData();
        } else {
          console.error('保存路由失败:', result.error);
          setError(result.error?.message || '保存失败');
        }
        return result;
      } catch (err) {
        console.error('保存路由异常:', err);
        setError('保存异常');
        return { status: 'error', error: { message: '保存异常' } };
      } finally {
        setLoading(false);
      }
    },
    [fetchRouterData],
  );

  /**
   * 删除路由
   */
  const deleteRouter = useCallback(
    async (routerId) => {
      setLoading(true);
      setError(null);
      try {
        const result = await routerDelete({ id: routerId });
        if (result.status === 'success') {
          // 删除成功后重新获取数据
          await fetchRouterData();
        } else {
          console.error('删除路由失败:', result.error);
          setError(result.error?.message || '删除失败');
        }
        return result;
      } catch (err) {
        console.error('删除路由异常:', err);
        setError('删除异常');
        return { status: 'error', error: { message: '删除异常' } };
      } finally {
        setLoading(false);
      }
    },
    [fetchRouterData],
  );

  /**
   * 获取产品线列表
   */
  const fetchProductLines = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const result = await apiService.productLine.getList();

      if (result.status === 'success') {
        setProductLines(result.data || []);
        return result.data || [];
      } else {
        console.error('获取产品线失败:', result.error);
        setError(result.error?.message || '获取产品线失败');
        setProductLines([]);
        return [];
      }
    } catch (err) {
      console.error('获取产品线异常:', err);
      setError('获取产品线异常');
      setProductLines([]);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * 获取产品模块列表
   */
  const fetchProductModules = useCallback(async (productLineId) => {
    if (!productLineId) {
      setProductModules([]);
      return [];
    }

    setLoading(true);
    setError(null);

    try {
      const result = await apiService.productModule.getList({ productLineId });

      if (result.status === 'success') {
        setProductModules(result.data || []);
        return result.data || [];
      } else {
        console.error('获取产品模块失败:', result.error);
        setError(result.error?.message || '获取产品模块失败');
        setProductModules([]);
        return [];
      }
    } catch (err) {
      console.error('获取产品模块异常:', err);
      setError('获取产品模块异常');
      setProductModules([]);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * 生成知识库
   */
  const generateKnowledgeBase = useCallback(async (params) => {
    setLoading(true);
    setError(null);

    try {
      const result = await apiService.knowledgeBase.generate(params);

      if (result.status !== 'success') {
        console.error('生成知识库失败:', result.error);
        setError(result.error?.message || '生成知识库失败');
      }

      return result;
    } catch (err) {
      console.error('生成知识库异常:', err);
      setError('生成知识库异常');
      return { status: 'error', error: { message: '生成知识库异常' } };
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * 导出知识库
   */
  const exportKnowledgeBase = useCallback(async (params) => {
    setLoading(true);
    setError(null);

    try {
      const result = await apiService.knowledgeBase.export(params);

      if (result.status !== 'success') {
        console.error('导出知识库失败:', result.error);
        setError(result.error?.message || '导出知识库失败');
      }

      return result;
    } catch (err) {
      console.error('导出知识库异常:', err);
      setError('导出知识库异常');
      return { status: 'error', error: { message: '导出知识库异常' } };
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * 更新单个路由数据
   */
  const updateSingleRouter = useCallback((routerId, updatedRouter) => {
    setRouterData((prev) =>
      prev.map((router) =>
        router.id === routerId ? { ...router, ...updatedRouter } : router,
      ),
    );
  }, []);

  /**
   * 删除路由数据
   */
  const removeRouterData = useCallback((routerId) => {
    setRouterData((prev) => prev.filter((router) => router.id !== routerId));
  }, []);

  // ==================== 业务逻辑方法 ====================

  /**
   * 处理编辑操作
   */
  const handleEdit = useCallback((record, type) => {
    setCurrentRecord({ ...record, type });
    setModalVisible(true);
  }, []);

  /**
   * 检查是否可以添加
   */
  const canAdd = useCallback(() => {
    if (activeTab === 'productLine') return true;
    if (activeTab === 'productModule') return !!selectedProductLine;
    if (activeTab === 'router') return !!selectedProductModule;
    if (activeTab === 'knowledgeBase') {
      return !!(selectedProductLine || selectedProductModule);
    }
    return false;
  }, [activeTab, selectedProductLine, selectedProductModule]);

  /**
   * 获取添加按钮文本
   */
  const getAddButtonText = useCallback(() => {
    const textMap = {
      productLine: '新增产品线',
      productModule: '新增产品模块',
      router: '新增路由',
      knowledgeBase: '编辑知识库',
    };
    return textMap[activeTab] || '新增';
  }, [activeTab]);

  /**
   * 获取路由统计信息
   */
  const getRouterStats = useCallback(() => {
    if (!selectedProductModule) {
      return { total: 0, withKnowledge: 0 };
    }

    // 确保 routerData 是数组类型
    const safeRouterData = Array.isArray(routerData) ? routerData : [];

    const total = safeRouterData.length;
    const withKnowledge = safeRouterData.filter(
      (r) => r.knowledgeBase && r.knowledgeBase.pageDescription,
    ).length;

    return { total, withKnowledge };
  }, [routerData, selectedProductModule]);

  /**
   * 重置所有状态
   */
  const reset = useCallback(() => {
    setActiveTab('productLine');
    setSelectedProductLine(null);
    setSelectedProductModule(null);
    setModalVisible(false);
    setCurrentRecord(null);
    setRouterData([]);
    setSiderCollapsed(false);
    setLoading(false);
    setError(null);
  }, []);

  // ==================== Context Value ====================

  const contextValue = {
    // 状态
    activeTab,
    selectedProductLine,
    selectedProductModule,
    modalVisible,
    currentRecord,
    routerData,
    productLines,
    productModules,
    siderCollapsed,
    loading,
    error,

    // 基础操作方法
    setActiveTab,
    setSelectedProductLine: handleSetSelectedProductLine,
    setSelectedProductModule: handleSetSelectedProductModule,
    setModalVisible: handleSetModalVisible,
    setCurrentRecord,
    setSiderCollapsed,
    toggleSiderCollapsed,
    setError,
    clearError,

    // 数据操作方法
    fetchRouterData,
    updateRouterData,
    addRouterData,
    saveOrUpdateRouter,
    deleteRouter,
    fetchProductLines,
    fetchProductModules,
    generateKnowledgeBase,
    exportKnowledgeBase,
    updateSingleRouter,
    removeRouterData,

    // 业务逻辑方法
    handleEdit,
    canAdd,
    getAddButtonText,
    getRouterStats,
    reset,
  };

  return (
    <RouterPathContext.Provider value={contextValue}>
      {children}
    </RouterPathContext.Provider>
  );
};

// ==================== 自定义Hooks ====================

/**
 * 获取基础状态的Hook
 */
export const useRouterPathState = () => {
  const context = useContext(RouterPathContext);
  if (!context) {
    throw new Error(
      'useRouterPathState must be used within a RouterPathProvider',
    );
  }

  return {
    activeTab: context.activeTab,
    selectedProductLine: context.selectedProductLine,
    selectedProductModule: context.selectedProductModule,
    modalVisible: context.modalVisible,
    currentRecord: context.currentRecord,
    routerData: context.routerData,
    productLines: context.productLines,
    productModules: context.productModules,
    siderCollapsed: context.siderCollapsed,
    loading: context.loading,
    error: context.error,
  };
};

/**
 * 获取操作方法的Hook
 */
export const useRouterPathActions = () => {
  const context = useContext(RouterPathContext);
  if (!context) {
    throw new Error(
      'useRouterPathActions must be used within a RouterPathProvider',
    );
  }

  return {
    setActiveTab: context.setActiveTab,
    setSelectedProductLine: context.setSelectedProductLine,
    setSelectedProductModule: context.setSelectedProductModule,
    setModalVisible: context.setModalVisible,
    setCurrentRecord: context.setCurrentRecord,
    setSiderCollapsed: context.setSiderCollapsed,
    toggleSiderCollapsed: context.toggleSiderCollapsed,
    setError: context.setError,
    clearError: context.clearError,
    fetchRouterData: context.fetchRouterData,
    updateRouterData: context.updateRouterData,
    addRouterData: context.addRouterData,
    saveOrUpdateRouter: context.saveOrUpdateRouter,
    deleteRouter: context.deleteRouter,
    fetchProductLines: context.fetchProductLines,
    fetchProductModules: context.fetchProductModules,
    generateKnowledgeBase: context.generateKnowledgeBase,
    exportKnowledgeBase: context.exportKnowledgeBase,
    updateSingleRouter: context.updateSingleRouter,
    removeRouterData: context.removeRouterData,
    handleEdit: context.handleEdit,
    canAdd: context.canAdd,
    getAddButtonText: context.getAddButtonText,
    getRouterStats: context.getRouterStats,
    reset: context.reset,
  };
};

export default RouterPathContext;
